

import { useState, useEffect, useMemo } from "react";
import {
  Search,
  Calendar,
  User,
  Activity,
  Filter,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Plus,
  Edit3,
  Trash2,
  Eye,
  Download,
  LogIn,
  Settings,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import { mastersApi } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/dateFormat";
import { useToast } from "@/context/ToastContext";

interface ActivityLog {
  id: number;
  user_id: number;
  action: string;
  loggable_type: string;
  loggable_id: number;
  changes: Record<string, any> | null;
  created_at: string;
  user?: { name: string; email: string };
}

interface ActionStat {
  action: string;
  count: number;
}

function StatBadge({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div className={`px-3 py-1.5 rounded-lg ${color} flex items-center gap-2`}>
      <span className="text-xs font-medium opacity-80">{label}:</span>
      <span className="text-sm font-bold">{value}</span>
    </div>
  );
}

function getActionIcon(action: string) {
  const actionLower = action.toLowerCase();
  if (actionLower.includes('create') || actionLower.includes('add')) return Plus;
  if (actionLower.includes('update') || actionLower.includes('edit')) return Edit3;
  if (actionLower.includes('delete') || actionLower.includes('remove')) return Trash2;
  if (actionLower.includes('view') || actionLower.includes('show')) return Eye;
  if (actionLower.includes('login') || actionLower.includes('auth')) return LogIn;
  if (actionLower.includes('export') || actionLower.includes('download')) return Download;
  if (actionLower.includes('setting')) return Settings;
  return Activity;
}

function getActionColor(action: string) {
  const actionLower = action.toLowerCase();
  if (actionLower.includes('create') || actionLower.includes('add')) return 'bg-emerald-100 text-emerald-700';
  if (actionLower.includes('update') || actionLower.includes('edit')) return 'bg-blue-100 text-blue-700';
  if (actionLower.includes('delete') || actionLower.includes('remove')) return 'bg-red-100 text-red-700';
  if (actionLower.includes('login') || actionLower.includes('auth')) return 'bg-purple-100 text-purple-700';
  if (actionLower.includes('export') || actionLower.includes('download')) return 'bg-amber-100 text-amber-700';
  return 'bg-stone-100 text-stone-700';
}

function formatEntityType(type: string) {
  return type
    .replace('App\\Models\\', '')
    .replace(/([A-Z])/g, ' $1')
    .trim();
}

export default function ActivityLogsPage() {
  const { showToast } = useToast();

  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [actions, setActions] = useState<ActionStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [stats, setStats] = useState({ total: 0, today: 0 });

  useEffect(() => {
    loadData();
    loadActions();
    loadStats();
  }, []);

  useEffect(() => {
    loadData();
  }, [currentPage, selectedAction, fromDate, toDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params: any = { page: currentPage };
      if (selectedAction) params.action = selectedAction;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (searchQuery) params.search = searchQuery;
      
      const response = await mastersApi.activityLogs.list(params);
      const data = response.data?.data;
      setLogs(data?.data || []);
      setLastPage(data?.last_page || 1);
      setTotal(data?.total || 0);
    } catch (error) {
      showToast("Failed to load activity logs", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadActions = async () => {
    try {
      const response = await mastersApi.activityLogs.actions();
      setActions(response.data?.data || []);
    } catch (error) {
      // Silent fail
    }
  };

  const loadStats = async () => {
    try {
      const response = await mastersApi.activityLogs.stats();
      setStats(response.data?.data || { total: 0, today: 0 });
    } catch (error) {
      // Silent fail
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadData();
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedAction("");
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
    loadData();
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  const filteredLogs = useMemo(() => {
    if (!searchQuery) return logs;
    const q = searchQuery.toLowerCase();
    return logs.filter(
      (log) =>
        log.action.toLowerCase().includes(q) ||
        log.user?.name?.toLowerCase().includes(q) ||
        formatEntityType(log.loggable_type).toLowerCase().includes(q)
    );
  }, [logs, searchQuery]);

  if (loading && logs.length === 0) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-4">
        <h1 className="text-xl font-bold text-stone-800">Activity Logs</h1>
        <p className="text-sm text-stone-500">Track all system activities and user actions.</p>
      </div>

      {/* Compact Stats */}
      <div className="flex flex-wrap gap-2 mb-4">
        <StatBadge label="Total" value={stats.total} color="bg-stone-100 text-stone-700" />
        <StatBadge label="Today" value={stats.today} color="bg-blue-100 text-blue-700" />
        <StatBadge label="Showing" value={`${logs.length} of ${total}`} color="bg-amber-100 text-amber-700" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-stone-200 p-3 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search logs..."
              className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            />
          </div>
          <select
            value={selectedAction}
            onChange={(e) => { setSelectedAction(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          >
            <option value="">All Actions</option>
            {actions.map((action) => (
              <option key={action.action} value={action.action}>
                {action.action} ({action.count})
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              placeholder="From"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
              placeholder="To"
            />
          </div>
          <button
            onClick={clearFilters}
            className="px-4 py-2 rounded-xl bg-stone-100 text-stone-600 font-medium text-sm hover:bg-stone-200 transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {filteredLogs.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Activity className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No activity logs found</p>
        </div>
      ) : (
        <>
          {/* Mobile Card Layout */}
          <div className="sm:hidden space-y-2">
            {filteredLogs.map((log) => {
              const ActionIcon = getActionIcon(log.action);
              return (
                <div key={log.id} className="bg-white rounded-xl border border-stone-200 p-3">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getActionColor(log.action)}`}>
                      <ActionIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                        <span className="text-xs text-stone-400">{formatTimeAgo(log.created_at)}</span>
                      </div>
                      <div className="text-sm text-stone-800 mb-1">
                        {formatEntityType(log.loggable_type)} #{log.loggable_id}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-stone-500">
                        <User className="w-3 h-3" />
                        {log.user?.name || 'System'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table Layout */}
          <div className="hidden sm:block bg-white rounded-xl border border-stone-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">Action</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">Entity</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">User</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-stone-500">Time</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-stone-500">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredLogs.map((log) => {
                    const ActionIcon = getActionIcon(log.action);
                    return (
                      <tr key={log.id} className="hover:bg-stone-50 transition-colors">
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getActionColor(log.action)}`}>
                              <ActionIcon className="w-4 h-4" />
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getActionColor(log.action)}`}>
                              {log.action}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-sm text-stone-800">{formatEntityType(log.loggable_type)}</div>
                          <div className="text-xs text-stone-400">ID: {log.loggable_id}</div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-stone-100 flex items-center justify-center">
                              <User className="w-3 h-3 text-stone-500" />
                            </div>
                            <span className="text-sm text-stone-600">{log.user?.name || 'System'}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-sm text-stone-600">{formatDateTime(log.created_at)}</div>
                          <div className="text-xs text-stone-400">{formatTimeAgo(log.created_at)}</div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {log.changes && Object.keys(log.changes).length > 0 ? (
                            <button
                              onClick={() => alert(JSON.stringify(log.changes, null, 2))}
                              className="text-xs px-2 py-1 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200"
                            >
                              View Changes
                            </button>
                          ) : (
                            <span className="text-xs text-stone-400">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {lastPage > 1 && (
            <div className="flex items-center justify-between mt-4 px-2">
              <div className="text-sm text-stone-500">
                Page {currentPage} of {lastPage}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded-lg bg-stone-100 text-stone-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-200"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))}
                  disabled={currentPage === lastPage}
                  className="px-3 py-1.5 rounded-lg bg-stone-100 text-stone-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-200"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
}
